import type { Router } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { usePermissions } from '@/composables/usePermissions';

declare global {
    interface Window {
        __SSR_HYDRATING__?: boolean;
    }
}

export function setupGuards(router: Router) {
    router.beforeEach(async (to, _from, next) => {
        if (import.meta.env.SSR) {
            return next();
        }

        const isHydrating = typeof window !== 'undefined' && window.__SSR_HYDRATING__ === true;

        const authStore = useAuthStore();
        const { canAll, canAny, hasAnyExact } = usePermissions();

        // During hydration, protected routes are an empty shell; public SSR routes
        // must retain their server-matched auth state until after mounting.
        const shouldLoadAuth = !authStore.token
            && typeof window !== 'undefined'
            && (!isHydrating || to.meta.requiresAuth === true);
        if (shouldLoadAuth) {
            await authStore.loadTokenFromStorage();
        }

        // Routes that require authentication
        if (to.meta.requiresAuth && !authStore.isAuthenticated) {
            if (typeof window !== 'undefined') {
                sessionStorage.setItem('auth_redirect_after_login', to.fullPath);
            }

            try {
                await authStore.initiateOAuthLogin();
                return next(false);
            } catch {
                return next({
                    name: 'login',
                    query: { redirect: to.fullPath },
                });
            }
        }

        const requiredPermissions = to.meta.permissions ?? [];
        const anyPermission = to.meta.anyPermission ?? [];
        const exactAnyPermission = to.meta.exactAnyPermission ?? [];
        const requiresPermissions = requiredPermissions.length > 0 || anyPermission.length > 0 || exactAnyPermission.length > 0;

        if (requiresPermissions) {
            const hasAllRequired = requiredPermissions.length === 0 || canAll(requiredPermissions);
            const hasAnyRequired = anyPermission.length === 0 || canAny(anyPermission);
            const hasAnyExactRequired = exactAnyPermission.length === 0 || hasAnyExact(exactAnyPermission);

            if (!hasAllRequired || !hasAnyRequired || !hasAnyExactRequired) {
                if (to.name === 'forbidden') {
                    return next({ name: 'home' });
                }

                return next({
                    name: 'forbidden',
                    query: {
                        from: to.fullPath,
                    },
                });
            }
        }

        // Routes only for guests (login/register)
        if (to.meta.guest && authStore.isAuthenticated) {
            return next({ name: 'home' });
        }

        next();
    });
}
